import { BottomSheet, CbhIcon, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";

interface CancellationPolicyInfoBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  onContinueToCancel: () => void;
  onGoBack: () => void;
}

export function CancellationPolicyInfoBottomSheet(props: CancellationPolicyInfoBottomSheetProps) {
  const { modalState, shiftId, onContinueToCancel, onGoBack } = props;

  useLogEffect(
    APP_V2_USER_EVENTS.GUIDED_SUPPORT_CANCELLATION_POLICY_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  return (
    <BottomSheet modalState={modalState}>
      <Stack spacing={5} sx={{ paddingX: 5, paddingTop: 8 }}>
        <Text semibold variant="h3">
          Need to cancel this shift?
        </Text>
        <Box
          component="ul"
          sx={(theme) => ({
            margin: 0,
            paddingLeft: theme.spacing(4),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          })}
        >
          <Box component="li">
            <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Cancelling early helps the workplace find a replacement
            </Text>
          </Box>
          <Box component="li">
            <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Cancel in the app — we&apos;ll guide you through it
            </Text>
          </Box>
          <Box component="li">
            <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Support agents follow this same process with no exceptions — cancelling in the app
              saves you time
            </Text>
          </Box>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={(theme) => ({
            border: `1px solid ${theme.palette.text.disabled}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(3),
            alignItems: "flex-start",
          })}
        >
          <CbhIcon type="info" size="small" color={(theme) => theme.palette.text.secondary} />
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Don&apos;t worry — on the next page, we&apos;ll walk you through your cancellation
            options and let you review any impact to your account before you confirm.
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="vertical" sx={{ marginTop: 8 }}>
        <Button fullWidth variant="destructive" size="large" onClick={onContinueToCancel}>
          Continue to cancel
        </Button>
        <Button fullWidth variant="contained" size="large" onClick={onGoBack}>
          I need help with something else
        </Button>
      </DialogFooter>
    </BottomSheet>
  );
}
